/**
 * Manifest schemas
 */

import type { CommandHotkey } from '@tunasong/schemas'
import {
  agentReadCursorsSchema,
  favoriteSchemaLegacy,
  hotkeySchema,
  maturitySchema,
  profileGearSchema,
} from '@tunasong/schemas'
import { z } from 'zod'

/**
 * Default keyboard shortcuts shipped with Tunasong. Exported so the runtime
 * can forward-merge new defaults onto existing users' persisted profiles —
 * Zod `.default(...)` only fires on a missing field, not on an existing array
 * with stale entries. See `mergeMissingHotkeys` in `@tunasong/redux`.
 */
export const defaultHotkeys: CommandHotkey[] = [
  { hotkey: 'opt+up', label: 'Block: Move Up', commandId: 'block-move-up' },
  { hotkey: 'opt+down', label: 'Block: Move Down', commandId: 'block-move-down' },
  { hotkey: 'shift+mod+d', label: 'Block: Duplicate', commandId: 'block-duplicate' },
  { hotkey: 'shift+mod+backspace', label: 'Block: Delete', commandId: 'block-delete' },
  { hotkey: 'mod+b', label: 'Format: Bold', commandId: 'format-bold' },
  { hotkey: 'mod+i', label: 'Format: Italic', commandId: 'format-italic' },
  { hotkey: 'tab', label: 'Suggestions: Insert', commandId: 'suggestions-insert' },
  { hotkey: 'mod+shift+j', label: 'Agent: Toggle Panel', commandId: 'agent-toggle-panel' },
  // Plain J asks here, Shift+J opens the panel (Joni; the "Ask AI" convention). `agent-ask-here` in plugin-agent.
  { hotkey: 'mod+j', label: 'Agent: Ask here', commandId: 'agent-ask-here' },
]

/** @todo these should be in the plugins and "injected" into the manifest (e.g., during build?) */
export const midiPluginSchema = z
  .object({
    midi: z.boolean().describe('MIDI support').default(false),
    btMidi: z.boolean().describe('Bluetooth MIDI support (experimental)').default(false),
    btMidiAutoConnect: z.boolean().describe('Auto-connect to paired Bluetooth MIDI devices').default(false),
  })
  .optional()

export const chordPlayerPluginSchema = z
  .object({
    play: z.boolean().describe('Play chords on selection').default(false),
  })
  .optional()

export const chordPluginSchema = z
  .object({
    showTranspose: z.boolean().describe('Show transposition with the chord, e.g., +2').default(true),
  })
  .optional()

export const playlistPluginSchema = z
  .object({
    closeNotesOnPageTurn: z
      .boolean()
      .describe(
        'In performance mode, a page-turn closes the open notes panel (bottom panel) so you can perform the song'
      )
      .default(false),
  })
  .optional()

export const externalToolSchema = z.object({
  name: z.string(),
  url: z.string().url(),
})

export type ExternalTool = z.infer<typeof externalToolSchema>

/**
 * Resolve the default `featureMaturity` lazily so the Vitest setup can opt every
 * test into `'labs'` via `TUNA_DEFAULT_FEATURE_MATURITY=labs`, exposing plugins
 * gated at `beta`/`labs` (e.g. plugin-agent) to the CI test surface. Production
 * code never sets this env var, so consumers continue to default to `'stable'`.
 *
 * Context: `plugin-agent` shipped at `maturity: 'beta'` and `createTunaEditor`
 * silently filtered it out for users on default `'stable'` settings. The bug
 * went undetected because Vitest also defaulted to `'stable'`, so no test
 * exercised the same pluginList real users saw. See
 * `apps/tunasong/app/src/di/primary-surface-maturity.test.ts` for the explicit
 * regression guard, and `infra/base-config/vitest.setup.js` for where the env
 * var is set.
 */
const resolveDefaultFeatureMaturity = (): 'labs' | 'beta' | 'stable' => {
  // Read `process.env` without depending on `@types/node` (manifest doesn't bring it in).
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
  const fromEnv = env?.TUNA_DEFAULT_FEATURE_MATURITY
  if (fromEnv === 'labs' || fromEnv === 'beta' || fromEnv === 'stable') {
    return fromEnv
  }
  return 'stable'
}

export const userConfigSchema = z.object({
  featureMaturity: maturitySchema.optional().default(resolveDefaultFeatureMaturity),
  theme: z.enum(['light', 'dark', 'system']).optional().default('system'),
  /** Favorites - entity IDs or page path (e.g., /app/guitarpatterns) of the user's favorites */
  favorites: z.array(favoriteSchemaLegacy).optional(),
  /** AI Configuration */
  ai: z.record(z.string(), z.unknown()).optional(),
  /** The keyboard shortcuts / hotkeys for the user. Format handled by the useHotkeys hook */
  hotkeys: z.array(hotkeySchema).default(defaultHotkeys),
  musicPreferences: z
    .object({
      transpose: z.number().optional(),
      noteFormat: z.enum(['standard', 'norwegian']).optional(), // Norwegian uses H instead of B
      /**
       * Theory-label vocabulary level — gates copy across every music-theory
       * learning surface (cadence chip, progression overlay, modal glow,
       * suggestion explainer, chord popup). When absent, consumers fall back
       * to 'intermediate' at the use site (no schema-level auto-migration).
       *
       * See docs/superpowers/specs/2026-05-16-music-theory-learning-phase-1-design.md.
       */
      theoryLevel: z
        .enum(['beginner', 'intermediate', 'advanced', 'off'])
        .describe('Theory-label vocabulary level — gates copy across all music-theory learning surfaces.')
        .optional(),
      /**
       * Default mode for new Melody Writer sessions. `guided` enables
       * layer-by-layer pedagogical scaffolding (chord tones first,
       * passing/neighbor next, etc.); `free-play` exposes all affordances
       * at once. Each melody-line stores its own `mode` field, so changing
       * this default does not retroactively re-mode existing melodies.
       *
       * See docs/superpowers/specs/2026-05-17-melody-writer-design.md.
       */
      melodyWriterMode: z
        .enum(['guided', 'free-play'])
        .describe('Default mode for new Melody Writer sessions.')
        .optional(),
      /**
       * Opt-in toggle for the in-editor voice-leading diff (the inter-chord
       * connector showing common tones held / steps / leaps). Off by default
       * even when `theoryLevel` is on: it is the densest per-pair surface, so
       * it requires its own explicit gate (Joni visual spec §4.2). Kept
       * `.optional()` (not `.default(false)`) to mirror `theoryLevel`: the
       * absence is resolved to `false` at the use site (`useShowVoiceMotion`),
       * so existing persisted configs need no migration and existing
       * `musicPreferences` literals stay valid.
       *
       * See docs/superpowers/specs/2026-06-06-voice-leading-diff-visual-spec.md.
       */
      showVoiceMotion: z
        .boolean()
        .describe('Opt-in: show the in-editor voice-leading diff between adjacent chords.')
        .optional(),
      /**
       * Per-concept distinct-use counts driving the learning rank (the Tuna
       * ladder). Each key is a `LearningConcept` id; the value is the count of
       * distinct in-song uses. Rank is always DERIVED from this via
       * `computeRank` at the use site — we never store the rank itself, and we
       * never migrate: absent means "no usage yet" (everyone starts Tinned
       * Tuna). `theoryLevel` above acts as the floor.
       *
       * See docs/superpowers/specs/2026-06-14-learning-system-with-levels-design.md.
       */
      conceptUsage: z
        .record(z.string(), z.number())
        .describe('Per-concept distinct-use counts driving the learning rank; rank is derived at the use site.')
        .optional(),
      /**
       * The singer's calibrated comfortable range, as fractional MIDI numbers — the median of a
       * sustained lowest note and a sustained highest one. Pitch Trainer transposes every
       * exercise into the middle 60 % of it, because without a range the tool measures tessitura
       * and reports it as ear: a phrase centred on A4 is comfortable for a soprano and
       * unsingable for a bass, and the bass's score would record the difference as a failure of
       * ear.
       *
       * `.optional()`, not `.default()`, for the same reason as `theoryLevel`: absence means
       * "uncalibrated — run calibration first" and is resolved at the use site, so no persisted
       * config is migrated and no existing `musicPreferences` literal breaks.
       *
       * See docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8.
       */
      vocalRange: z
        .object({ low: z.number(), high: z.number() })
        .describe("The singer's calibrated comfortable range in MIDI numbers; absent means uncalibrated.")
        .optional(),
      /**
       * Input devices for which the user has confirmed they are wearing headphones.
       *
       * Melody capture plays a click through the whole take, and audio capture segments whatever
       * the microphone hears into notes. A click coming out of a speaker is therefore not a
       * nuisance — it is written into the melody as notes, on the beat, every beat. So the capture
       * UI will not open the microphone until the user has confirmed, and the confirmation is
       * stored per device label because it is a fact about a physical setup: confirming for an
       * audio interface says nothing about the laptop's built-in microphone.
       *
       * `.optional()`, not `.default()`, for the same reason as `vocalRange`: absence means "never
       * confirmed", which is resolved at the use site, so no persisted config is migrated and no
       * existing `musicPreferences` literal breaks.
       *
       * See docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5.
       */
      headphoneConfirmedDevices: z
        .array(z.string())
        .describe('Input device labels for which the user has confirmed headphones are in use.')
        .optional(),
      /*
       * There is deliberately NO `inputLatencyByDevice` here. It was added for the Melody
       * Trainer's §6.3 tap test and removed with it: the taps were timed against count-in clicks
       * routed through `engine.play` while the reference a singer performs against goes out
       * through `playScheduled`, so the stored number was measured on one audio path and
       * subtracted from takes captured against another. Timing is now reported relative to each
       * phrase's own mean, which needs no stored calibration - and a stale one left in the config
       * would quietly shift scores for anyone who had run the old test (Bowie, 2026-09-13).
       */
    })
    .optional(),
  /** Gear requirements for technical riders */
  gear: profileGearSchema.optional(),
  /**
   * Performance mode preferences. Private: `publicProfileSchema` never projects them.
   *
   * Part sets are stored as plain strings and read through `normalizeParts` (@tunasong/schemas), and
   * every field degrades to absent via `.catch`: `ConfigService.init` drops the WHOLE config on a
   * failed parse, so one part this build does not know must never cost the user their settings.
   *
   * See docs/ux/2026-10-12-performance-roles-and-notes-ux.md.
   */
  performance: z
    .object({
      defaultParts: z
        .array(z.string())
        .describe('Parts used for personal songs and for bands without an explicit set.')
        .optional()
        .catch(undefined),
      /** Bandspace id → the parts this person explicitly chose for that band. Never written from the default. */
      partsByBand: z
        .record(z.string(), z.array(z.string()))
        .describe('Explicit part set per bandspace id.')
        .optional()
        .catch(undefined),
      /** Bandspace ids where the one-time "Playing X, your default part." notice has been shown. */
      acknowledgedDefaultBands: z
        .array(z.string())
        .describe('Bands where the default-parts notice has been shown.')
        .optional()
        .catch(undefined),
      /** Bandspace ids where this person does not perform (manager, producer). Projected per band only. */
      notPerformingBands: z
        .array(z.string())
        .describe('Bands this person does not perform with.')
        .optional()
        .catch(undefined),
      /** "My notes" on or off in Perform, remembered per user. Absent means off. */
      notesVisible: z.boolean().describe('Show my notes in Perform.').optional().catch(undefined),
      /** Where notes show in Perform. Absent means beside the song. */
      notesPlacement: z
        .enum(['beside', 'panel'])
        .describe('Show my notes beside the song or in a panel.')
        .optional()
        .catch(undefined),
    })
    .optional()
    .catch(undefined),
  /** External tools - user-configured links to external services */
  externalTools: z.array(externalToolSchema).optional().default([]),
  /**
   * Per-user, per-agent "last read" timestamps. Drives the Agent Rail's
   * unread-dot derivation. Cross-device synced via profile push.
   */
  agentReadCursors: agentReadCursorsSchema.optional(),
  plugins: z
    .lazy(() =>
      z
        .object({
          // The key is the plugin key / id
          midi: midiPluginSchema,
          'chord-player': chordPlayerPluginSchema,
          chord: chordPluginSchema,
          playlist: playlistPluginSchema,
        })
        .loose()
    )
    .optional()
    .default({}),
})
